"use client";
import React, { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import EnlargeIcon from "@/assets/icons/enlarge.svg";
import FullScreenModal from "../ArtworkMedia/FullScreenModal/FullScreenModal";
import { ArtworkMedia } from "../ArtworkMedia";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import { useMutation } from "@tanstack/react-query";
import nftApi from "@/api/nfts/client";

export interface Props {
  nftId: string;
  mediaSrc: string;
  audioSrc?: string | null;
  type: string;
}

const FullScreen = ({ mediaSrc, audioSrc, type, nftId }: Props) => {
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [shouldShowAudioGuide, setShouldShowAudioGuide] = React.useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { mutateAsync: setNftMaximized } = useMutation({
    mutationFn: (nftId: string) => nftApi.setNftMaximized(nftId),
  });

  const showAudioGuide = () => {
    timeoutRef.current && clearTimeout(timeoutRef.current);
    setShouldShowAudioGuide(true);
  };

  const hideAudioGuide = () => {
    timeoutRef.current = setTimeout(() => setShouldShowAudioGuide(false), 2000);
  };

  useEffect(() => {
    if (isFullScreen) {
      setNftMaximized(nftId);
    }
  }, [isFullScreen, nftId, setNftMaximized]);

  return (
    <>
      <FullScreenModal
        isOpen={isFullScreen}
        onClose={() => setIsFullScreen(false)}
      >
        <ArtworkMedia
          media={mediaSrc}
          type={type}
          fullScreenEnabled={false}
          className="w-auto h-full flex justify-center align-middle"
        />
        <div
          onMouseEnter={() => showAudioGuide()}
          onMouseLeave={() => hideAudioGuide()}
          className="absolute bottom-0 left-0 right-0 h-[100px]"
        >
          <div
            className={twMerge(
              !shouldShowAudioGuide && "opacity-0 transition-all",
              "bg-[#000a] h-[100px] flex flex-col justify-center overflow-hidden",
            )}
          >
            <AudioPlayer
              src={audioSrc}
              isFullScreenOpen={isFullScreen}
              className={twMerge("bg-transparent")}
            />
          </div>
        </div>
      </FullScreenModal>

      <div
        className="group cursor-pointer hidden md:block absolute w-full h-full z-10 padding-inherit"
        onClick={() => setIsFullScreen(true)}
      >
        <div
          className={twMerge(
            "transition-color duration-200 group-hover:bg-overlay flex flex-col items-center justify-center w-full h-full box-border",
          )}
        >
          <EnlargeIcon className="hidden group-hover:block w-10 h-10 transition group-duration-300 mb-2" />
          <span className="hidden group-hover:block text-white text-body-4 tracking-wide uppercase transition-all font-normal group-duration-300">
            Click to enlarge
          </span>
        </div>
      </div>
    </>
  );
};

export default FullScreen;
